
import React, { useState, useRef, useEffect } from 'react';
import { getRoofingAdvice } from '../services/geminiService';

interface Message {
  role: 'user' | 'bot';
  text: string;
}

const AIChat: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: 'bot', text: 'Hi! I\'m your Margate Roofing AI Assistant. How can I help with your roof today?' }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const handleSend = async () => {
    if (!input.trim() || isTyping) return;
    
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setIsTyping(true);

    const advice = await getRoofingAdvice(userMsg);
    setMessages(prev => [...prev, { role: 'bot', text: advice }]);
    setIsTyping(false);
  };

  return (
    <div className={`fixed bottom-6 right-6 z-[60] transition-all duration-300 ${isOpen ? '' : 'mb-20 md:mb-0'}`}>
      {isOpen ? (
        <div className="bg-white dark:bg-gray-800 w-[calc(100vw-3rem)] sm:w-[380px] h-[500px] max-h-[70vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700">
          <div className="bg-primary p-4 flex items-center justify-between text-white">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center font-bold text-xs">AI</div>
              <div>
                <p className="font-bold text-sm">Roofing Assistant</p>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                  <p className="text-[10px] text-white/80">Online</p>
                </div>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="hover:bg-white/10 p-1 rounded transition-colors text-xl font-bold leading-none">
              &times;
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 flex flex-col gap-4 bg-gray-50 dark:bg-background-dark/50">
            {messages.map((m, i) => (
              <div key={i} className={`max-w-[85%] rounded-2xl p-3 text-sm ${
                m.role === 'user' 
                  ? 'bg-primary text-white self-end rounded-tr-none' 
                  : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 self-start rounded-tl-none border border-gray-100 dark:border-gray-600 shadow-sm'
              }`}>
                {m.text}
              </div>
            ))}
            {isTyping && (
              <div className="bg-white dark:bg-gray-700 text-gray-400 self-start rounded-2xl rounded-tl-none p-3 border border-gray-100 dark:border-gray-600 animate-pulse text-xs">
                Typing...
              </div>
            )}
          </div>

          <div className="p-4 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 flex gap-2">
            <input 
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Ask a question..."
              className="flex-1 bg-gray-100 dark:bg-gray-900 border-none rounded-xl px-4 py-2 text-sm focus:ring-1 focus:ring-primary dark:text-white"
            />
            <button 
              onClick={handleSend}
              className="bg-primary text-white px-3 py-2 rounded-xl hover:bg-blue-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
            </button>
          </div>
        </div>
      ) : (
        <button 
          onClick={() => setIsOpen(true)}
          className="bg-primary text-white w-14 h-14 rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-transform group animate-bounce font-black text-lg"
        >
          ?
          <span className="absolute -top-1 -right-1 flex h-4 w-4">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
            <span className="relative inline-flex rounded-full h-4 w-4 bg-accent"></span>
          </span>
        </button>
      )}
    </div>
  );
};

export default AIChat;
